import { Component, computed, DestroyRef, inject, input, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { LangChangeEvent, TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexGrid,
  ApexLegend,
  ApexMarkers,
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  ApexStates,
  ChartComponent
} from 'ng-apexcharts';
import { catchError, distinctUntilChanged, filter, map, Observable, of, startWith, switchMap } from 'rxjs';
import { BalanceService } from 'core/services/balance.service';
import { InvitationsService } from 'core/services/invitations.service';
import {
  AnalyticsLanguage,
  AnalyticsTrendDirection,
  IMemberContributedCommissionsResponse,
  IMemberReferralGrowthResponse,
  IMemberSproutedSeedsResponse
} from 'core/types/my-forest-analytics.interface';
import { UserAvatarV2Component } from 'shared/components/user-avatar-v2/user-avatar-v2.component';
import { KorviuLoaderComponent } from 'shared/components/korviu-loader/korviu-loader.component';
import { ThousandsSeparatorPipe } from 'shared';
import { User } from 'shared/types/user.model';
import { AnalyticsTrendIconComponent } from './analytics-trend-icon.component';
import {
  BaseChartOptions,
  DEFAULT_CHART_OPTIONS,
  NO_PERSISTENT_CHART_SELECTION_EVENTS,
  NO_PERSISTENT_CHART_SELECTION_STATES
} from 'shared/types/chart.config';

export type DonutChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  colors: string[];
  labels: string[];
  plotOptions: ApexPlotOptions;
  dataLabels: ApexDataLabels;
  fill: ApexFill;
  stroke: ApexStroke;
  legend: ApexLegend;
  states: ApexStates;
};

export type LineChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  stroke: ApexStroke;
  dataLabels: ApexDataLabels;
  markers: ApexMarkers;
  colors: string[];
  grid: ApexGrid;
  legend: ApexLegend;
  tooltip: ApexTooltip;
  states: ApexStates;
};

type AnalyticsState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: T }
  | { status: 'error' };

interface IAnalyticsRequest {
  publicId: string;
  lang: AnalyticsLanguage;
}

@Component({
  selector: 'app-my-forest-user-info-special',
  imports: [
    TranslatePipe,
    UserAvatarV2Component,
    ThousandsSeparatorPipe,
    ChartComponent,
    KorviuLoaderComponent,
    AnalyticsTrendIconComponent
  ],
  templateUrl: './my-forest-user-info-special.component.html',
  styleUrl: './my-forest-user-info-special.component.scss'
})
export class MyForestUserInfoSpecialComponent {
  readonly #invitationsService = inject(InvitationsService);
  readonly #balanceService = inject(BalanceService);
  readonly #translateService = inject(TranslateService);
  readonly #destroyRef = inject(DestroyRef);

  readonly user = input<User>();

  readonly sproutedSeedsState = signal<AnalyticsState<IMemberSproutedSeedsResponse>>({ status: 'idle' });
  readonly referralGrowthState = signal<AnalyticsState<IMemberReferralGrowthResponse>>({ status: 'idle' });
  readonly contributedCommissionsState = signal<AnalyticsState<IMemberContributedCommissionsResponse>>({
    status: 'idle'
  });

  readonly sproutedSeedsData = computed(() => this.#getStateData(this.sproutedSeedsState()));
  readonly referralGrowthData = computed(() => this.#getStateData(this.referralGrowthState()));
  readonly contributedCommissionsData = computed(() => this.#getStateData(this.contributedCommissionsState()));

  readonly sproutedSeedsLoading = computed(() => this.sproutedSeedsState().status === 'loading');
  readonly sproutedSeedsError = computed(() => this.sproutedSeedsState().status === 'error');
  readonly sproutedSeedsEmpty = computed(() => {
    const data = this.sproutedSeedsData();
    return !!data && data.total_invitations === 0;
  });
  readonly referralGrowthLoading = computed(() => this.referralGrowthState().status === 'loading');
  readonly referralGrowthError = computed(() => this.referralGrowthState().status === 'error');
  readonly referralGrowthEmpty = computed(() => {
    const series = this.referralGrowthData()?.series;
    return !!series && series.every(item => item.sent === 0 && item.registered === 0 && item.card_activations === 0);
  });
  readonly contributedCommissionsLoading = computed(() => this.contributedCommissionsState().status === 'loading');
  readonly contributedCommissionsError = computed(() => this.contributedCommissionsState().status === 'error');
  readonly contributedCommissionsEmpty = computed(() => {
    const series = this.contributedCommissionsData()?.series;
    return !!series && series.every(item => item.ganas === 0 && Number(item.usd) === 0);
  });

  readonly sproutedSeedsTrendDirection = computed(() => this.sproutedSeedsData()?.last_month.trend ?? 'flat');
  readonly sproutedSeedsTrendClass = computed(() =>
    this.#getTrendTextClass(this.sproutedSeedsData()?.last_month.trend)
  );
  readonly contributedCommissionsTrendDirection = computed(
    () => this.contributedCommissionsData()?.trend.direction ?? 'flat'
  );
  readonly contributedCommissionsTrendClass = computed(() =>
    this.#getTrendTextClass(this.contributedCommissionsData()?.trend.direction)
  );
  readonly contributedCommissionsTrendPercentage = computed(() =>
    Math.abs(this.contributedCommissionsData()?.trend.change_pct ?? 0)
  );
  readonly contributedCommissionsTrendKey = computed(() => {
    const direction = this.contributedCommissionsData()?.trend.direction;

    if (direction === 'down') {
      return 'my-forest.trendingDownBy';
    }

    if (direction === 'flat') {
      return 'my-forest.trendUnchanged';
    }

    return 'my-forest.trendingUpBy';
  });

  readonly #currentLanguage = toSignal(
    this.#translateService.onLangChange.pipe(map((event: LangChangeEvent) => this.#normalizeLanguage(event.lang))),
    {
      initialValue: this.#normalizeLanguage(
        this.#translateService.currentLang || this.#translateService.defaultLang || 'es'
      )
    }
  );
  readonly #analyticsRequest = computed<IAnalyticsRequest | null>(() => {
    const publicId = this.user()?.publicId;

    return publicId ? { publicId, lang: this.#currentLanguage() } : null;
  });

  constructor() {
    this.#subscribeToAnalytics(
      (publicId: string) => this.#invitationsService.getMemberSproutedSeeds(publicId),
      this.sproutedSeedsState
    );
    this.#subscribeToAnalytics(
      (publicId: string, lang: AnalyticsLanguage) =>
        this.#invitationsService.getMemberReferralGrowth(publicId, 12, lang),
      this.referralGrowthState
    );
    this.#subscribeToAnalytics(
      (publicId: string, lang: AnalyticsLanguage) =>
        this.#balanceService.getMemberContributedCommissions(publicId, 'community', 6, lang),
      this.contributedCommissionsState
    );
  }

  public sendWhatsAppMessage(): void {
    const phoneNumber = this.user()?.fullPhoneNumber;
    if (this.user()?.isDeleted || !phoneNumber) {
      return;
    }
    const whatsappDesktopUrl = `whatsapp://send?phone=${phoneNumber}`;

    window.open(whatsappDesktopUrl, '_blank');
  }

  readonly donutChartOptions = computed<DonutChartOptions>(() => {
    const data = this.sproutedSeedsData();
    // Recompute translated labels when the active language changes.
    this.#currentLanguage();

    return {
      series: [data?.accepted ?? 0, data?.pending ?? 0],
      colors: ['#007DCB', '#8AC3E7'],
      labels: [
        this.#translateService.instant('my-forest.accepted'),
        this.#translateService.instant('my-forest.pending')
      ],

      chart: {
        type: 'donut',
        height: 250,
        width: 250,
        events: NO_PERSISTENT_CHART_SELECTION_EVENTS
      },

      dataLabels: { enabled: false },
      legend: { show: false },
      stroke: { width: 0 },

      plotOptions: {
        pie: {
          donut: {
            size: '60%',
            labels: {
              show: false,
              name: { show: false },
              value: {
                show: false
              },
              total: {
                show: false
              }
            }
          }
        }
      },

      fill: { opacity: 1 },
      states: NO_PERSISTENT_CHART_SELECTION_STATES
    };
  });

  readonly lineChartOptions = computed<LineChartOptions>(() => {
    const data = this.referralGrowthData()?.series ?? [];
    // Recompute translated series names and number formatting on language changes.
    const language = this.#currentLanguage();

    return {
      series: [
        {
          name: this.#translateService.instant('my-forest.sent'),
          data: data.map(item => item.sent)
        },
        {
          name: this.#translateService.instant('my-forest.registered'),
          data: data.map(item => item.registered)
        },
        {
          name: this.#translateService.instant('my-forest.cardActivations'),
          data: data.map(item => item.card_activations)
        }
      ],
      chart: {
        type: 'line',
        height: 250,
        zoom: { enabled: false },
        toolbar: {
          tools: {
            download: false
          }
        },
        events: NO_PERSISTENT_CHART_SELECTION_EVENTS
      },
      stroke: {
        curve: 'smooth',
        width: 3
      },
      colors: ['#EF6401', '#007DCB', '#0D9443'],
      dataLabels: {
        enabled: false
      },
      markers: {
        size: 0
      },
      grid: {
        borderColor: '#e7e7e7',
        row: {
          colors: ['transparent'],
          opacity: 0.5
        }
      },
      yaxis: {
        labels: {
          style: {
            colors: '#8A8E94', // цвет шрифта для Y
            fontSize: '0.9375rem',
            fontFamily: 'Outfit, sans-serif'
          }
        }
      },
      xaxis: {
        categories: data.map(item => item.label),
        labels: {
          style: {
            colors: '#8A8E94',
            fontSize: '1.125rem',
            fontFamily: 'Outfit, sans-serif'
          }
        }
      },
      legend: {
        show: false
      },
      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        x: {
          show: true
        },
        y: {
          formatter: (value: number) => value.toLocaleString(language)
        },
        marker: {
          show: true
        }
      },
      states: NO_PERSISTENT_CHART_SELECTION_STATES
    };
  });

  readonly earningsGrowthChartOptions = computed<BaseChartOptions>(() => {
    const data = this.contributedCommissionsData()?.series ?? [];
    const categories = data.map(item => item.label);
    const seriesData = data.map(item => {
      const value = Number.parseFloat(item.usd);
      return Number.isFinite(value) ? value : 0;
    });
    const barHeight = 32;
    const gap = 0;
    const numberOfBars = Math.max(categories.length, 1);
    const chartHeight = barHeight * numberOfBars + gap * (numberOfBars - 1);
    return {
      ...DEFAULT_CHART_OPTIONS,
      chart: {
        ...DEFAULT_CHART_OPTIONS.chart,
        type: 'bar',
        height: chartHeight,
        width: '100%'
      },
      states: {
        /* normal: {
          filter: {
            type: 'none'
          }
        }, */
        hover: {
          filter: {
            type: 'none'
          }
        },
        active: {
          allowMultipleDataPointsSelection: false,
          filter: {
            type: 'none'
          }
        }
      },
      series: [
        {
          name: this.#translateService.instant('my-forest.contributedCommissions'),
          data: seriesData
        }
      ],
      xaxis: {
        ...DEFAULT_CHART_OPTIONS.xaxis,
        categories,
        labels: {
          show: true,
          style: {
            fontSize: '16px',
            colors: '#737980',
            fontFamily: 'Outfit'
          }
        },
        axisBorder: { show: false },
        axisTicks: { show: false }
      },
      yaxis: {
        ...DEFAULT_CHART_OPTIONS.yaxis,
        min: 0,
        forceNiceScale: true,
        decimalsInFloat: 0,
        labels: {
          show: true,
          formatter: (value: number) => Math.round(value).toLocaleString(),
          style: {
            colors: '#737980',
            fontSize: '12px',
            fontFamily: 'Outfit'
          }
        },
        axisBorder: { show: false },
        axisTicks: { show: false }
      },

      grid: {
        show: true,
        borderColor: '#EEEEEF',
        strokeDashArray: 4,
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } }
      },

      plotOptions: {
        ...DEFAULT_CHART_OPTIONS.plotOptions,
        bar: {
          ...DEFAULT_CHART_OPTIONS.plotOptions.bar,
          horizontal: false,
          borderRadius: 4,
          columnWidth: '48px',
          dataLabels: {
            position: 'top'
          }
        }
      },
      dataLabels: {
        ...DEFAULT_CHART_OPTIONS.dataLabels,
        enabled: true,
        offsetY: -25,
        formatter: (value: number) => {
          return this.#formatCurrency(value);
        },
        style: {
          fontSize: '16px',
          fontFamily: 'Outfit',
          fontWeight: '400',
          colors: ['#505760']
        }
      },
      tooltip: {
        ...DEFAULT_CHART_OPTIONS.tooltip,
        enabled: true,
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const value = series[seriesIndex][dataPointIndex] ?? 0;
          const category = w.config.xaxis?.categories[dataPointIndex] ?? '';
          const displayValue = this.#formatCurrency(Number(value));
          return `
					<div class="px-3 py-2 flex flex-col custom-tooltip">
					  <div class="text-sm md:text-base font-medium outfit title">${category}</div>
					  <div class="paragraph-small-card font-normal outfit value">${displayValue}</div>
					</div>
				  `;
        }
      },
      responsive: [
        {
          breakpoint: 540,
          options: {
            plotOptions: {
              bar: {
                horizontal: true,
                barHeight: `${barHeight}px`
              }
            },
            dataLabels: {
              enabled: true,
              offsetX: 35,
              offsetY: 0,
              formatter: (value: number) => this.#formatCurrency(value),
              style: {
                fontSize: '14px',
                fontFamily: 'Outfit',
                fontWeight: '400',
                colors: ['#505760']
              }
            },
            xaxis: {
              labels: {
                show: true,
                style: {
                  fontSize: '12px',
                  fontFamily: 'Outfit'
                }
              }
            },
            yaxis: {
              labels: {
                show: true,
                style: {
                  fontSize: '14px',
                  fontFamily: 'Outfit'
                }
              }
            }
          }
        }
      ]
    };
  });

  #subscribeToAnalytics<T>(
    requestFactory: (publicId: string, lang: AnalyticsLanguage) => Observable<T>,
    state: WritableSignal<AnalyticsState<T>>
  ): void {
    toObservable(this.#analyticsRequest)
      .pipe(
        filter((request): request is IAnalyticsRequest => request !== null),
        distinctUntilChanged(
          (previous, current) => previous.publicId === current.publicId && previous.lang === current.lang
        ),
        switchMap(({ publicId, lang }) =>
          requestFactory(publicId, lang).pipe(
            map((data: T): AnalyticsState<T> => ({ status: 'success', data })),
            catchError(() => of<AnalyticsState<T>>({ status: 'error' })),
            startWith<AnalyticsState<T>>({ status: 'loading' })
          )
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((result: AnalyticsState<T>) => state.set(result));
  }

  #getStateData<T>(state: AnalyticsState<T>): T | null {
    return state.status === 'success' ? state.data : null;
  }

  #normalizeLanguage(lang: string): AnalyticsLanguage {
    return lang === 'en' ? 'en' : 'es';
  }

  #getTrendTextClass(direction?: AnalyticsTrendDirection): string {
    if (direction === 'up') {
      return 'text-[#078339]';
    }

    if (direction === 'down') {
      return 'text-[#CB2222]';
    }

    return 'text-[#737980]';
  }

  #formatCurrency(value: number): string {
    return `$${value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }
}
